// ===========================================
// Screenshot capture for web / WebOS players
// ===========================================
//
// Composites whatever is currently on screen (video frames + visible
// <img> elements + overlay canvas layers) onto a single canvas, encodes
// as PNG, and uploads via `POST /device/screenshot` — matching the
// Android player's screenshot RPC so the dashboard "Capture screenshot"
// button and the WhatsApp /screenshot command work cross-platform.
//
// Avoids html2canvas / dom-to-image because they add ~600KB to the
// bundle and don't materially help: the player only ever displays a
// small fixed set of layered DOM nodes (video, img, overlay canvas) we
// already control.
//
// Limitations vs. a real screen-capture API:
//   - DOM-rendered text overlays (widget HTML, scrolling tickers) are
//     NOT captured by drawImage(<div>). For text-heavy widgets the
//     screenshot will show the layout but lose text content. Acceptable
//     for ops debugging (90% of screenshots are about content / scheduling
//     issues, not text-rendering bugs).
//   - Cross-origin images on the canvas would taint it and block
//     toBlob(). All our assets come from the same R2/Supabase storage
//     origin, which we serve with CORS headers, so this is fine in
//     production. Falls back to a "tainted" stub if it triggers.

import { api } from '../utils/api';

const STUB_WIDTH = 1920;
const STUB_HEIGHT = 1080;
const JPEG_QUALITY = 0.85;

interface CaptureResult {
  blob: Blob;
  width: number;
  height: number;
}

/**
 * Walk the DOM and composite the currently visible video + img layers
 * onto a single canvas. Returns the resulting blob + dimensions.
 * Throws if the canvas ends up tainted (cross-origin asset without CORS).
 */
async function capture(): Promise<CaptureResult> {
  const w = window.innerWidth || STUB_WIDTH;
  const h = window.innerHeight || STUB_HEIGHT;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  // Solid black background — matches the player's default look so
  // letterboxed content looks right.
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, w, h);

  // Sort layers by stacking order so the screenshot composes correctly.
  // We treat anything matched by these selectors as a paintable layer.
  // Order matters: video first (background), images on top, overlay canvas
  // last.
  const videos = Array.from(document.querySelectorAll('video')) as HTMLVideoElement[];
  const images = Array.from(document.querySelectorAll('img')) as HTMLImageElement[];
  const overlayCanvases = Array.from(document.querySelectorAll('canvas')).filter(
    (c) => c !== canvas,
  ) as HTMLCanvasElement[];

  // Layer painter: respects element opacity + position via
  // getBoundingClientRect (handles CSS transforms in our zone layout).
  // Skips elements with opacity 0 or display:none.
  const paint = (el: HTMLElement, drawFn: () => void) => {
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return;
    const opacity = parseFloat(style.opacity || '1');
    if (opacity <= 0) return;
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, opacity));
    try {
      drawFn();
    } catch (err) {
      // One bad element shouldn't kill the screenshot.
      console.warn('[screenshot] paint failed for element:', err);
    }
    ctx.restore();
  };

  for (const video of videos) {
    if (video.readyState < 2 /* HAVE_CURRENT_DATA */) continue;
    const rect = video.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    paint(video, () => {
      ctx.drawImage(video, rect.left, rect.top, rect.width, rect.height);
    });
  }

  for (const img of images) {
    if (!img.complete || img.naturalWidth === 0) continue;
    const rect = img.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    paint(img, () => {
      ctx.drawImage(img, rect.left, rect.top, rect.width, rect.height);
    });
  }

  for (const oc of overlayCanvases) {
    if (oc.width === 0 || oc.height === 0) continue;
    const rect = oc.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    paint(oc, () => {
      ctx.drawImage(oc, rect.left, rect.top, rect.width, rect.height);
    });
  }

  // Encode to JPEG to keep upload size reasonable for kiosks on slow
  // links. PNG would balloon to ~5 MB for 1080p; JPEG ~150 KB at q=0.85.
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  if (!blob) {
    // toBlob returns null when the canvas is tainted (cross-origin
    // assets without CORS). Caller can fall back or surface an error.
    throw new Error('Canvas toBlob returned null — likely tainted by cross-origin asset');
  }
  return { blob, width: w, height: h };
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') return reject(new Error('Reader returned non-string'));
      // result is a data URL — strip the prefix to get just the base64.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Capture + upload. Called from the COMMAND_SCREENSHOT WS handler in
 * player.ts. Logs (does not throw) on failure so a screenshot request
 * never crashes the player.
 */
export async function captureAndUploadScreenshot(): Promise<void> {
  console.log('[screenshot] capture starting');
  let result: CaptureResult;
  try {
    result = await capture();
  } catch (err) {
    console.error('[screenshot] capture failed:', err);
    return;
  }

  try {
    const base64 = await blobToBase64(result.blob);
    await api.uploadScreenshot({
      image: base64,
      mimeType: 'image/jpeg',
      width: result.width,
      height: result.height,
      capturedAt: new Date().toISOString(),
    });
    console.log(`[screenshot] uploaded ${result.width}x${result.height} (${Math.round(result.blob.size / 1024)} KB)`);
  } catch (err) {
    console.error('[screenshot] upload failed:', err);
  }
}
